/**
 * gift-cart-tier.js
 * Gère l'ajout/retrait automatique du cadeau quand le seuil du panier est dépassé.
 *
 * Ce fichier est chargé GLOBALEMENT (pas dans innerHTML), donc il s'exécute.
 * Il lit les paramètres depuis les data-attributes du .cart-tier-progress-wrapper
 * qui est injecté via le snippet cart-tier-progress.liquid.
 *
 * Problème résolu : les scripts inline dans les snippets Liquid sont ignorés
 * par le thème Focal car il injecte le contenu via innerHTML.
 */

((document) => {
    'use strict';

    const NS = '[CartTier]';

    /**
     * Lit les paramètres du snippet depuis les data-attributes du wrapper
     */
    function getConfig() {
        const wrapper = document.querySelector('.cart-tier-progress-wrapper');
        if (!wrapper) {
            return null;
        }
        const threshold = parseInt(wrapper.getAttribute('data-threshold'), 10);
        const giftId = parseInt(wrapper.getAttribute('data-gift-id'), 10);
        if (!threshold || !giftId) return null;
        return { threshold, giftId };
    }

    /**
     * Rafraîchit le drawer mini-cart via le mécanisme natif Focal
     * (CartDrawer._rerenderCart) et met à jour le compteur du header.
     */
    function refreshDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
            window.location.reload();
            return;
        }
        
        // Délai de 500ms pour éviter la collision (race condition) avec le rafraîchissement natif de Focal
        setTimeout(() => {
            // Déclenche le re-render natif du thème Focal
            drawer.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { replacementDelay: 400 }
            }));
            // Met à jour le compteur d'articles dans le header
            fetch('/cart.js')
                .then(res => res.json())
                .then(cart => {
                    document.documentElement.dispatchEvent(
                        new CustomEvent('cart:updated', {
                            bubbles: true,
                            detail: { cart }
                        })
                    );
                })
                .catch(err => {});
        }, 500);
    }

    /**
     * Calcule le sous-total du panier en excluant le produit cadeau (GIFT_ID)
     * pour éviter d'influencer le calcul du seuil.
     */
    function getNonGiftSubtotal(cart, giftId) {
        return (cart.items || []).reduce((sum, item) => {
            const isGiftProduct = (item.variant_id == giftId || item.id == giftId);
            const isOfficialGift = item.properties && item.properties._is_gift === 'true';
            const isCouponGift = item.properties && item.properties._source === 'coupon_gift';
            
            if (isGiftProduct && (isOfficialGift || isCouponGift)) {
                return sum;
            }
            return sum + item.final_line_price;
        }, 0);
    }

    let isUpdating = false;
    let pendingCart = null;

    async function checkGift(cart) {
        // Re-lire la configuration à chaque appel (au cas où le wrapper ait changé)
        const config = getConfig();
        if (!config) return;
        if (isUpdating) return;

        const { threshold: THRESHOLD, giftId: GIFT_ID } = config;

        // Calculer le total du panier sans les produits cadeaux
        const total = getNonGiftSubtotal(cart, GIFT_ID);
        
        // Trouver tous les items correspondant au produit cadeau
        const giftItems = (cart.items || []).filter(item => item.variant_id == GIFT_ID || item.id == GIFT_ID);
        // Trouver le cadeau officiel (avec la propriété _is_gift: 'true')
        const officialGiftItem = giftItems.find(item => item.properties && item.properties._is_gift === 'true');

        // AJOUTER / AJUSTER le cadeau (Seuil dépassé)
        if (total >= THRESHOLD) {
            if (!officialGiftItem) {
                isUpdating = true;
                try {
                    // L'ancienne logique supprimait toutes les variantes payantes. On ne le fait plus.

                    // Ajouter le cadeau officiel (quantité 1)
                    const response = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            items: [{ id: GIFT_ID, quantity: 1, properties: { '_is_gift': 'true' } }]
                        })
                    });
                    if (response.ok) {
                        await fetch('/discount/GIFT-SECRET-779338892');
                        // On force un recalcul du panier par Shopify pour prendre en compte le code
                        await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: {} }) });
                        refreshDrawer();
                    } else {
                        window.location.reload();
                    }
                } catch (e) {
                    console.error(e);
                }
                isUpdating = false;
            } else {
                // Le cadeau officiel existe déjà. On vérifie et force la normalisation des quantités
                const updates = {};
                let needsUpdate = false;

                // Forcer la quantité du cadeau officiel à exactement 1
                if (officialGiftItem.quantity !== 1) {
                    updates[officialGiftItem.key] = 1;
                    needsUpdate = true;
                }

                // Supprimer les autres lignes du même produit UNIQUEMENT si ce sont d'autres cadeaux (doublons)
                giftItems.forEach(item => {
                    if (item.key !== officialGiftItem.key) {
                        const isOfficialGift = item.properties && item.properties._is_gift === 'true';
                        if (isOfficialGift) {
                            updates[item.key] = 0;
                            needsUpdate = true;
                        }
                    }
                });

                if (needsUpdate) {
                    isUpdating = true;
                    try {
                        const response = await fetch('/cart/update.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ updates })
                        });
                        if (response.ok) {
                            refreshDrawer();
                        } else {
                            window.location.reload();
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    isUpdating = false;
                }
            }
        }

        // RETIRER le cadeau (Seuil non atteint)
        else {
            if (officialGiftItem) {
                isUpdating = true;
                try {
                    const response = await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: String(officialGiftItem.key), quantity: 0 })
                    });
                    if (response.ok) {
                        refreshDrawer();
                    } else {
                        window.location.reload();
                    }
                } catch (e) {
                    console.error(e);
                }
                isUpdating = false;
            }
        }
    }

    /**
     * Vérifie que le wrapper est présent dans le DOM
     * avant de démarrer. Utile car le wrapper n'existe pas
     * tant que le drawer n'a pas été ouvert une première fois.
     */
    function tryInit() {
        if (getConfig()) {

            // 1. Vérification immédiate
            // Ajout d'un délai de 800ms pour laisser le temps au tiroir de s'ouvrir complètement
            // et éviter d'être ignoré par le thème Focal
            setTimeout(() => {
                if (pendingCart) {
                    checkGift(pendingCart);
                    pendingCart = null;
                } else {
                    fetch('/cart.js')
                        .then(res => res.json())
                        .then(cart => {
                            checkGift(cart);
                        })
                        .catch(err => {});
                }
            }, 800);

            return true;
        }
        return false;
    }

    // Écouter les mises à jour globalement dès le chargement du script
    document.documentElement.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
            const config = getConfig();
            if (config) {
                checkGift(event.detail.cart);
            } else {
                pendingCart = event.detail.cart;
            }
        }
    });

    // Tant que le drawer n'a pas été ouvert, le wrapper n'existe pas.
    // On attend que le DOM change pour essayer d'initialiser.
    if (!tryInit()) {
        const observer = new MutationObserver(() => {
            if (tryInit()) {
                observer.disconnect();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

})(document);